import React from 'react'
import { api } from '../lib/api'
import Loading from '../ui/Loading'
import { useAuth } from '../auth/AuthContext'

function fmt(dt) {
    if (!dt) return '-'
    try {
        const d = new Date(dt)
        if (Number.isNaN(d.getTime())) return String(dt)
        const yyyy = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        const hh = String(d.getHours()).padStart(2, '0')
        const mi = String(d.getMinutes()).padStart(2, '0')
        return `${yyyy}-${mm}-${dd} ${hh}:${mi}`
    } catch {
        return String(dt)
    }
}

function formatStatLine(img) {
    const v = Number(img?.viewCount ?? 0)
    const d = Number(img?.downloadCount ?? 0)
    return `조회 ${Number.isFinite(v) ? v : 0} · 다운로드 ${Number.isFinite(d) ? d : 0}`
}

/** 목록 API의 linkedMessageTemplateCount 기준 — 없거나 구버전이면 false */
function hasLinkedMessageTemplates(img) {
    const n = Number(img?.linkedMessageTemplateCount)
    return Number.isFinite(n) && n > 0
}

function downloadBlob(blob, filename) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename || 'download'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}

/**
 * 카테고리 탭(고객안부·금융 등) 기반 이미지 자료실. 프롬프트 생성/기록은 운영자만.
 */
export default function SalesImagesGeminiLibrary() {
    const { user } = useAuth()
    const isOperator = Boolean((user?.roles || []).includes('OPERATOR'))

    const [activeTab, setActiveTab] = React.useState('고객안부')
    const [images, setImages] = React.useState([])
    const [loading, setLoading] = React.useState(false)
    const [error, setError] = React.useState(null)
    const [promptModalOpen, setPromptModalOpen] = React.useState(false)
    const [generatedPrompt, setGeneratedPrompt] = React.useState('')
    const [generatingPrompt, setGeneratingPrompt] = React.useState(false)
    const [generatingImage, setGeneratingImage] = React.useState(false)
    const [currentCategory, setCurrentCategory] = React.useState('고객안부')
    const [modelCandidates, setModelCandidates] = React.useState([])
    const [selectedModel, setSelectedModel] = React.useState('gemini-3.1-flash-image-preview')
    /** 'gemini' | 'openai' — 서버에서 Gemini vs OpenAI Images API(gpt-image-2) 분기 */
    const [imageProvider, setImageProvider] = React.useState('gemini')
    /** OpenAI Images API: low | medium | high | auto */
    const [openaiImageQuality, setOpenaiImageQuality] = React.useState('medium')
    /** OpenAI Images API size (서버 화이트리스트와 동일한 값만) */
    const [openaiImageSize, setOpenaiImageSize] = React.useState('1024x1024')
    const [loadingModels, setLoadingModels] = React.useState(false)
    const [modelFailureLines, setModelFailureLines] = React.useState([])
    const [imagePreviewUrls, setImagePreviewUrls] = React.useState({})
    const [promptSubcategory, setPromptSubcategory] = React.useState('')
    const [promptKeyMessage, setPromptKeyMessage] = React.useState('')
    const [promptAudience, setPromptAudience] = React.useState('')
    const [promptTone, setPromptTone] = React.useState('')
    const [promptStyle, setPromptStyle] = React.useState('충격통계')
    const [promptTopic, setPromptTopic] = React.useState('계절·시즌')
    const [promptCardCount, setPromptCardCount] = React.useState('1')
    const [promptRatio, setPromptRatio] = React.useState('1:1')
    const [promptProcessType, setPromptProcessType] = React.useState('플라이어')
    const [planningPrompt, setPlanningPrompt] = React.useState('')
    const [historyOpen, setHistoryOpen] = React.useState(false)
    const [promptHistoryOpen, setPromptHistoryOpen] = React.useState(false)
    const [selectedHistory, setSelectedHistory] = React.useState(null)
    const [historyPromptTab, setHistoryPromptTab] = React.useState('planning')
    const [imageZoomOpen, setImageZoomOpen] = React.useState(false)
    const [zoomImageUrl, setZoomImageUrl] = React.useState('')
    const [zoomImage, setZoomImage] = React.useState(null)
    const [zoomCaptionDraft, setZoomCaptionDraft] = React.useState('')
    const [savingCaption, setSavingCaption] = React.useState(false)
    const [deletingId, setDeletingId] = React.useState(null)
    const [uploadModalOpen, setUploadModalOpen] = React.useState(false)
    const [uploadCategory, setUploadCategory] = React.useState('고객안부')
    const [uploadCaption, setUploadCaption] = React.useState('')
    const [uploadFile, setUploadFile] = React.useState(null)
    const [uploadSubmitting, setUploadSubmitting] = React.useState(false)
    const [successMsg, setSuccessMsg] = React.useState(null)
    const [linkedMsgOpen, setLinkedMsgOpen] = React.useState(false)
    const [linkedMsgLoading, setLinkedMsgLoading] = React.useState(false)
    const [linkedMsgRows, setLinkedMsgRows] = React.useState([])
    const [linkedMsgError, setLinkedMsgError] = React.useState(null)
    const [linkedMsgCopiedId, setLinkedMsgCopiedId] = React.useState(null)
    const [reviewStatusFilter, setReviewStatusFilter] = React.useState('APPROVED')
    const [reviewingId, setReviewingId] = React.useState(null)
    const [autoGenerating, setAutoGenerating] = React.useState(false)

    const tabs = ['고객안부', '금융', '보험', '상품', '소식지']
    const [newsletterTypeFilter, setNewsletterTypeFilter] = React.useState('ALL')
    const now = new Date()
    const [newsletterYearFilter, setNewsletterYearFilter] = React.useState(now.getFullYear())
    const [newsletterMonthFilter, setNewsletterMonthFilter] = React.useState(now.getMonth() + 1)
    const fixedModelOptions = [
        { value: 'gemini-3.1-flash-image-preview', label: 'Gemini 3.1 Flash Image Preview (기본)' },
        { value: 'gemini-2.5-flash-image', label: 'Gemini 2.5 Flash Image' },
        { value: 'gemini-3.1-pro-image', label: 'Gemini 3.1 Pro Image' }
    ]
    const openAiModelOptions = [{ value: 'gpt-image-2', label: 'GPT Image 2 (OpenAI)' }]
    const openAiQualityOptions = ['low', 'medium', 'high', 'auto']
    const openAiSizeOptions = [
        '1024x1024',
        '1024x1536',
        '1536x1024',
        '2048x2048',
        '2048x1152',
        '3840x2160',
        '2160x3840',
        'auto'
    ]
    const subcategoryPresetsByCategory = {
        고객안부: ['계절 안부', '건강 안부', '명절 안부', '장마/폭염 안부', '연말 안부'],
        금융: ['연금', 'IRP', 'ISA', '절세', '은퇴 준비', '가계부채 점검', '퇴직연금 이전', '금리 이슈 대응'],
        보험: ['실손보험', '암보험', '운전자보험', '치아보험', '유병자보험', '간병보험', '교통사고 보상', '후유장해 보상', '입원/통원 청구'],
        상품: ['이번달 추천상품', '신규 출시', '갱신 안내', '특약 강화', '가족 보장 패키지', '비교설명 포인트', '상담유도형 카드'],
        소식지: ['이번달 하이라이트', '시장/금리 이슈', '신상품/특약 소개', '청구·보상 안내', '건강/검진 시즌', '규제·세제 변경', '고객 안부형 헤드라인']
    }
    /** 카테고리별 하위 주제(프롬프트 topic) — 세부 주제 preset과 함께 카테고리 전환 시 갱신 */
    const topicOptionsByCategory = {
        고객안부: ['계절·시즌', '건강·루틴', '명절·기념일', '날씨·안전 안내', '감사·응원', '일상 소통'],
        금융: ['재테크', '연금·노후', '세금·절세', '투자·시장', '부동산', '대출·부채', '은퇴 설계'],
        보험: ['건강·실손', '암·중대질병', '사고·상해', '운전·교통', '가족·자녀', '노후·간병', '청구·보상'],
        상품: ['신상품 소개', '갱신·만기', '특약·옵션', '비교·설명', '패키지·프로모션', '상담 유도'],
        소식지: ['시장·이슈', '상품 소식', '규제·정책', '고객 안내', '시즌 캠페인', '건강·라이프']
    }
    const styleOptions = ['충격통계', '공감질문', '체크리스트', '안부터치']
    const audienceOptions = ['2030', '3040', '5060', '전체']
    const cardCountOptions = ['1', '2', '3', '4']
    const ratioOptions = ['1:1', '4:5', '9:16', '16:9']
    const processTypeOptions = ['플라이어', '보상']

    const loadImages = React.useCallback(async (category) => {
        setLoading(true)
        setError(null)
        try {
            const params = { category }
            params.reviewStatus = isOperator ? reviewStatusFilter : 'APPROVED'
            if (category === '소식지') {
                params.newsletterType = newsletterTypeFilter
                params.year = newsletterYearFilter
                params.month = newsletterMonthFilter
            }
            const res = await api.get('/gemini/images', { params })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            setImages(Array.isArray(payload) ? payload : [])
        } catch (_) {
            setError('이미지 목록을 불러오지 못했습니다.')
        } finally {
            setLoading(false)
        }
    }, [newsletterTypeFilter, newsletterYearFilter, newsletterMonthFilter, isOperator, reviewStatusFilter])

    React.useEffect(() => {
        void loadImages(activeTab)
    }, [activeTab, loadImages])

    React.useEffect(() => {
        let cancelled = false
        const currentUrls = []

        async function loadPreviews() {
            const next = {}
            for (const img of images) {
                if (!img?.fileId) continue
                try {
                    const res = await api.get(`/files/${img.fileId}/preview`, { responseType: 'blob' })
                    const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'image/png' })
                    const url = URL.createObjectURL(blob)
                    currentUrls.push(url)
                    next[img.id] = url
                } catch (_) {
                    // ignore
                }
            }
            if (!cancelled) {
                setImagePreviewUrls(prev => {
                    Object.values(prev || {}).forEach((u) => { try { URL.revokeObjectURL(u) } catch { } })
                    return next
                })
            } else {
                currentUrls.forEach((u) => { try { URL.revokeObjectURL(u) } catch { } })
            }
        }

        void loadPreviews()

        return () => {
            cancelled = true
        }
    }, [images])

    const applyPromptCategory = (category) => {
        setCurrentCategory(category)
        const subList = subcategoryPresetsByCategory[category] || []
        setPromptSubcategory((prev) => (subList.includes(prev) ? prev : ''))
        const topicList = topicOptionsByCategory[category] || []
        setPromptTopic((prev) => (topicList.includes(prev) ? prev : (topicList[0] || '')))
    }

    const loadGeminiModelCandidates = React.useCallback(async () => {
        setLoadingModels(true)
        try {
            const mres = await api.get('/gemini/models')
            const candidates = mres?.data?.data?.imageCandidates
            const dynamic = Array.isArray(candidates) ? candidates : []
            const merged = [...fixedModelOptions.map(x => x.value)]
            dynamic.forEach((m) => {
                if (!merged.includes(m)) merged.push(m)
            })
            setModelCandidates(merged)
        } catch (_) {
            setModelCandidates(fixedModelOptions.map(x => x.value))
        } finally {
            setLoadingModels(false)
        }
    }, [])

    const openPromptModal = async (category) => {
        setCurrentCategory(category)
        setPromptModalOpen(true)
        setGeneratedPrompt('')
        setPlanningPrompt('')
        const topicList = topicOptionsByCategory[category] || []
        setPromptTopic(topicList[0] || '')
        setPromptSubcategory('')
        setPromptKeyMessage('')
        setError(null)
        setModelFailureLines([])
        setImageProvider('gemini')
        setOpenaiImageQuality('medium')
        setOpenaiImageSize('1024x1024')
        setSelectedModel('gemini-3.1-flash-image-preview')
        setModelCandidates([])
        await loadGeminiModelCandidates()
    }

    const handleGeneratePrompt = async (category) => {
        setGeneratingPrompt(true)
        setError(null)
        try {
            const res = await api.post('/gemini/prompts/generate', {
                category,
                style: promptStyle || null,
                topic: promptTopic || null,
                subcategory: promptSubcategory || null,
                keyMessage: promptKeyMessage || null,
                audience: promptAudience || null,
                tone: promptTone || null,
                cardCount: promptCardCount || '1',
                ratio: promptRatio || '1:1',
                processType: promptProcessType || '플라이어'
            })
            const prompt = res?.data?.data?.prompt || res?.data?.prompt || ''
            const planning = res?.data?.data?.planningPrompt || ''
            setPlanningPrompt(planning)
            setGeneratedPrompt(prompt)
        } catch (e) {
            setError('프롬프트 생성에 실패했습니다: ' + (e?.response?.data?.message || e.message))
        } finally {
            setGeneratingPrompt(false)
        }
    }

    const handlePresetSubcategory = (value) => {
        setPromptSubcategory(value || '')
        if (!promptKeyMessage || !promptKeyMessage.trim()) {
            setPromptKeyMessage(value ? `${value} 핵심 안내` : '')
        }
    }

    const handleGenerateImage = async () => {
        if (!generatedPrompt || generatedPrompt.trim() === '') {
            setError('프롬프트가 없습니다. 먼저 프롬프트를 생성해주세요.')
            return
        }
        setGeneratingImage(true)
        setError(null)
        setModelFailureLines([])
        setSuccessMsg(null)
        const n = Math.min(4, Math.max(1, Number.parseInt(String(promptCardCount || '1'), 10) || 1))
        try {
            const res = await api.post('/gemini/images/generate', {
                prompt: generatedPrompt,
                category: currentCategory,
                model: selectedModel || (imageProvider === 'openai' ? 'gpt-image-2' : 'gemini-3.1-flash-image-preview'),
                planningPrompt: planningPrompt || null,
                renderPrompt: generatedPrompt || null,
                imageProvider: imageProvider || 'gemini',
                cardCount: n,
                ...(imageProvider === 'openai'
                    ? { openaiImageQuality: openaiImageQuality || 'medium', openaiImageSize: openaiImageSize || '1024x1024' }
                    : {})
            })
            const d = res?.data?.data
            if (d?.images && Array.isArray(d.images) && d.images.length > 1) {
                setSuccessMsg(`${d.imageCount || d.images.length}장의 카드 이미지가 자료실에 저장되었습니다.`)
                window.setTimeout(() => setSuccessMsg(null), 6000)
            } else {
                setSuccessMsg('이미지가 자료실에 저장되었습니다.')
                window.setTimeout(() => setSuccessMsg(null), 4000)
            }
            setPromptModalOpen(false)
            setGeneratedPrompt('')
            void loadImages(activeTab)
        } catch (e) {
            const msg = e?.response?.data?.message || e.message
            setError('이미지 생성에 실패했습니다: ' + msg)
            const raw = String(msg || '')
            const lines = raw.split('|').map(s => s.trim()).filter(s => s.startsWith('[') && s.endsWith(']'))
            setModelFailureLines(lines.map(s => s.slice(1, -1)))
        } finally {
            setGeneratingImage(false)
        }
    }

    const closeImageZoom = () => {
        setImageZoomOpen(false)
        setZoomImage(null)
        setZoomImageUrl('')
        setZoomCaptionDraft('')
    }

    const saveZoomCaption = async () => {
        if (!zoomImage?.id || !isOperator) return
        setSavingCaption(true)
        setError(null)
        try {
            await api.patch(`/gemini/images/${zoomImage.id}`, { caption: zoomCaptionDraft })
            setImages((prev) => prev.map((x) => (x.id === zoomImage.id ? { ...x, caption: zoomCaptionDraft.trim() || null } : x)))
            setZoomImage((z) => (z && z.id === zoomImage.id ? { ...z, caption: zoomCaptionDraft.trim() || null } : z))
        } catch (e) {
            const msg = e?.response?.data?.message || e.message
            setError('캡션 저장에 실패했습니다: ' + msg)
        } finally {
            setSavingCaption(false)
        }
    }

    const openUploadModal = () => {
        setUploadCategory(activeTab)
        setUploadCaption('')
        setUploadFile(null)
        setUploadModalOpen(true)
        setError(null)
    }

    const submitLibraryUpload = async () => {
        if (!uploadFile) {
            setError('업로드할 이미지 파일을 선택해주세요.')
            return
        }
        setUploadSubmitting(true)
        setError(null)
        try {
            const fd = new FormData()
            fd.append('file', uploadFile)
            fd.append('category', uploadCategory)
            if (uploadCaption.trim()) {
                fd.append('caption', uploadCaption.trim())
            }
            await api.post('/gemini/images/upload', fd)
            setUploadModalOpen(false)
            setUploadFile(null)
            setUploadCaption('')
            setSuccessMsg('이미지가 자료실에 업로드되었습니다.')
            window.setTimeout(() => setSuccessMsg(null), 4000)
            void loadImages(activeTab)
        } catch (e) {
            const msg = e?.response?.data?.message || e.message
            setError('업로드에 실패했습니다: ' + msg)
        } finally {
            setUploadSubmitting(false)
        }
    }

    const reviewImageEntry = async (img, action) => {
        if (!img?.id || reviewingId != null) return
        const label = action === 'approve' ? '승인' : '반려'
        const ok = window.confirm(`이 자동 생성 이미지를 ${label}할까요?`)
        if (!ok) return
        setReviewingId(img.id)
        setError(null)
        try {
            await api.post(`/gemini/images/${img.id}/${action}`)
            setSuccessMsg(action === 'approve' ? '이미지가 자료실에 승인되었습니다.' : '이미지가 반려되었습니다.')
            window.setTimeout(() => setSuccessMsg(null), 4000)
            void loadImages(activeTab)
        } catch (e) {
            const msg = e?.response?.data?.message || e.message
            setError(`${label}에 실패했습니다: ${msg}`)
        } finally {
            setReviewingId(null)
        }
    }

    const runAutoGenerationNow = async () => {
        if (autoGenerating) return
        const ok = window.confirm(`${activeTab} 카테고리에 맞는 이미지와 연결 문구 자동 후보를 만들까요? 오늘 이미 검수 대기/승인 후보가 있으면 새로 만들지 않습니다.`)
        if (!ok) return
        setAutoGenerating(true)
        setError(null)
        setSuccessMsg(null)
        try {
            const res = await api.post('/gemini/images/auto-generate', null, { params: { category: activeTab } })
            const payload = res?.data?.data || {}
            const created = Number(payload.created || 0)
            const skipped = Boolean(payload?.result?.skipped)
            setReviewStatusFilter('PENDING_REVIEW')
            setSuccessMsg(created > 0
                ? `${activeTab} 자동 후보가 생성되었습니다.`
                : (skipped ? `${activeTab}에는 오늘 이미 검수 대기 또는 승인된 자동 후보가 있습니다.` : `${activeTab} 자동 후보가 생성되지 않았습니다.`))
            window.setTimeout(() => setSuccessMsg(null), 5000)
            if (reviewStatusFilter === 'PENDING_REVIEW') {
                void loadImages(activeTab)
            }
        } catch (e) {
            const msg = e?.response?.data?.message || e.message
            setError('자동 생성 실행에 실패했습니다: ' + msg)
        } finally {
            setAutoGenerating(false)
        }
    }

    const openImageZoom = (img) => {
        if (!img) return
        setZoomImage(img)
        setZoomCaptionDraft(img.caption || '')
        setZoomImageUrl(imagePreviewUrls[img.id] || '')
        setImageZoomOpen(true)
        if (img.id == null) return
        void api.post(`/gemini/images/${img.id}/view`).then((res) => {
            const d = res?.data?.data
            if (!d || typeof d !== 'object') return
            const vc = d.viewCount
            const dc = d.downloadCount
            setImages((prev) => prev.map((x) => (x.id === img.id ? { ...x, viewCount: vc, downloadCount: dc } : x)))
            setZoomImage((z) => (z && z.id === img.id ? { ...z, viewCount: vc, downloadCount: dc } : z))
        }).catch(() => {})
    }

    const deleteImageEntry = async (img) => {
        if (!img?.id || deletingId != null) return
        const ok = window.confirm('이 이미지를 자료실에서 삭제할까요? 삭제 후에는 복구할 수 없습니다.')
        if (!ok) return
        setDeletingId(img.id)
        setError(null)
        try {
            await api.delete(`/gemini/images/${img.id}`)
            const u = imagePreviewUrls[img.id]
            if (u) {
                try { URL.revokeObjectURL(u) } catch { /* ignore */ }
            }
            if (zoomImage?.id === img.id) {
                closeImageZoom()
            }
            void loadImages(activeTab)
        } catch (e) {
            const msg = e?.response?.data?.message || e.message
            setError('삭제에 실패했습니다: ' + msg)
        } finally {
            setDeletingId(null)
        }
    }

    const openLinkedMessagesModal = async (imageId) => {
        if (imageId == null) return
        setLinkedMsgOpen(true)
        setLinkedMsgLoading(true)
        setLinkedMsgError(null)
        setLinkedMsgRows([])
        try {
            const res = await api.get(`/gemini/images/${imageId}/linked-message-templates`)
            const payload = res?.data?.data !== undefined ? res.data.data : res.data
            setLinkedMsgRows(Array.isArray(payload) ? payload : [])
        } catch (e) {
            const msg = e?.response?.data?.message || e.message
            setLinkedMsgError(msg || '연결된 메시지를 불러오지 못했습니다.')
        } finally {
            setLinkedMsgLoading(false)
        }
    }

    const closeLinkedMessagesModal = () => {
        setLinkedMsgOpen(false)
        setLinkedMsgRows([])
        setLinkedMsgError(null)
        setLinkedMsgCopiedId(null)
    }

    const copyLinkedMessageTemplate = async (row) => {
        if (!row) return
        const title = row.title || '제목 없음'
        const body = row.contentPreview != null && String(row.contentPreview).length > 0 ? String(row.contentPreview) : ''
        const text = body ? `${title}\n\n${body}` : title
        try {
            await navigator.clipboard.writeText(text)
        } catch {
            try {
                const ta = document.createElement('textarea')
                ta.value = text
                ta.setAttribute('readonly', '')
                ta.style.position = 'fixed'
                ta.style.left = '-9999px'
                document.body.appendChild(ta)
                ta.select()
                document.execCommand('copy')
                ta.remove()
            } catch {
                return
            }
        }
        setLinkedMsgCopiedId(row.id)
        window.setTimeout(() => {
            setLinkedMsgCopiedId((id) => (id === row.id ? null : id))
        }, 2000)
    }

    const downloadZoomFile = async () => {
        if (!zoomImage?.fileId) return
        try {
            const res = await api.get(`/files/${zoomImage.fileId}/download`, { responseType: 'blob' })
            const blob = new Blob([res.data], { type: res.headers?.['content-type'] || 'application/octet-stream' })
            const cd = res.headers?.['content-disposition'] || ''
            const m = /filename="?([^";]+)"?/i.exec(cd)
            const fallback = zoomImage.imageName || `image_${zoomImage.id}.png`
            const filename = m ? m[1] : fallback
            downloadBlob(blob, filename)
            const id = zoomImage.id
            setImages((prev) => prev.map((x) => (x.id === id ? { ...x, downloadCount: (Number(x.downloadCount) || 0) + 1 } : x)))
            setZoomImage((z) => (z && z.id === id ? { ...z, downloadCount: (Number(z.downloadCount) || 0) + 1 } : z))
        } catch (_) {
            setError('파일 다운로드에 실패했습니다.')
        }
    }

    return (
        <div className="space-y-4">
            <div className="flex gap-2 border-b mb-4 flex-wrap">
                {tabs.map(tab => (
                    <button
                        key={tab}
                        type="button"
                        className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                            activeTab === tab
                                ? 'border-brand-navy text-brand-navy'
                                : 'border-transparent text-slate-600 hover:text-slate-900'
                        }`}
                        onClick={() => setActiveTab(tab)}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {isOperator && (
                <div className="mb-4">
                    <div className="flex gap-2 flex-wrap items-center">
                        <button
                            type="button"
                            className="rounded bg-teal-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-800"
                            onClick={() => openPromptModal(activeTab)}
                        >
                            프롬프트 생성하기
                        </button>
                        <button
                            type="button"
                            className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                            onClick={() => setHistoryOpen((v) => !v)}
                        >
                            {historyOpen ? '프롬프트 기록 닫기' : '프롬프트 기록 보기'}
                        </button>
                        <button
                            type="button"
                            className="rounded border border-emerald-700 text-emerald-800 px-4 py-2 text-sm hover:bg-emerald-50"
                            onClick={() => openUploadModal()}
                        >
                            이미지 직접 업로드
                        </button>
                        <button
                            type="button"
                            className="rounded border border-amber-600 px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                            disabled={autoGenerating}
                            onClick={() => void runAutoGenerationNow()}
                        >
                            {autoGenerating ? '자동 생성 중…' : `${activeTab} 자동 후보 생성`}
                        </button>
                        <div className="flex rounded border bg-white p-1 text-xs">
                            {[
                                ['APPROVED', '승인 자료'],
                                ['PENDING_REVIEW', '검수 대기'],
                                ['REJECTED', '반려']
                            ].map(([value, label]) => (
                                <button
                                    key={value}
                                    type="button"
                                    className={`rounded px-3 py-1.5 font-medium ${
                                        reviewStatusFilter === value
                                            ? 'bg-slate-900 text-white'
                                            : 'text-slate-600 hover:bg-slate-50'
                                    }`}
                                    onClick={() => setReviewStatusFilter(value)}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {activeTab === '소식지' && (
                <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-2">
                    <select className="rounded border px-3 py-2 text-sm" value={newsletterTypeFilter} onChange={(e) => setNewsletterTypeFilter(e.target.value)}>
                        <option value="ALL">전체</option>
                        <option value="LIFE">생보</option>
                        <option value="FIRE">손보</option>
                    </select>
                    <input className="rounded border px-3 py-2 text-sm" type="number" value={newsletterYearFilter} onChange={(e) => setNewsletterYearFilter(Number(e.target.value || now.getFullYear()))} />
                    <select className="rounded border px-3 py-2 text-sm" value={newsletterMonthFilter} onChange={(e) => setNewsletterMonthFilter(Number(e.target.value))}>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => <option key={m} value={m}>{m}월</option>)}
                    </select>
                </div>
            )}

            {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
            {successMsg && <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{successMsg}</div>}
            {loading && <Loading />}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {images.length === 0 && !loading && (
                    <div className="col-span-full text-center text-slate-500 py-8">
                        등록된 이미지가 없습니다.
                    </div>
                )}
                {images.map(img => (
                    <div key={img.id} className="rounded border bg-white overflow-hidden">
                        {imagePreviewUrls[img.id] ? (
                            <img
                                src={imagePreviewUrls[img.id]}
                                alt={img.category}
                                className="w-full h-48 object-cover cursor-zoom-in"
                                onClick={() => openImageZoom(img)}
                                onError={(e) => {
                                    e.target.style.display = 'none'
                                }}
                            />
                        ) : (
                            <div className="w-full h-48 bg-slate-100 text-slate-500 text-xs flex items-center justify-center">
                                이미지 미리보기 준비 중
                            </div>
                        )}
                        <div className="p-2 text-xs text-slate-600">
                            <div className="flex flex-wrap gap-1 mb-1">
                                {img.reviewStatus && img.reviewStatus !== 'APPROVED' && (
                                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                                        img.reviewStatus === 'PENDING_REVIEW'
                                            ? 'bg-amber-100 text-amber-800'
                                            : 'bg-red-100 text-red-800'
                                    }`}>
                                        {img.reviewStatus === 'PENDING_REVIEW' ? '검수 대기' : '반려'}
                                    </span>
                                )}
                                {img.autoGenerated && (
                                    <span className="rounded bg-sky-100 text-sky-800 px-1.5 py-0.5 text-[10px] font-medium">
                                        자동 생성
                                    </span>
                                )}
                                {img.seriesCardIndex != null && img.imageSeriesId && (
                                    <span className="rounded bg-slate-100 text-slate-700 px-1.5 py-0.5 text-[10px] font-medium">
                                        카드 {img.seriesCardIndex}
                                    </span>
                                )}
                            </div>
                            {img.imageName && <div className="font-medium text-slate-800">{img.imageName}</div>}
                            {img.generationTopic && <div className="text-slate-700 mt-1">주제: {img.generationTopic}</div>}
                            {img.caption && <div className="text-slate-700 mt-1 line-clamp-3 whitespace-pre-wrap">{img.caption}</div>}
                            {img.insurerName && <div>보험사: {img.insurerName}</div>}
                            <div>{fmt(img.createdAt)}</div>
                            <div className="text-slate-500 mt-0.5">{formatStatLine(img)}</div>
                            <button
                                type="button"
                                disabled={!hasLinkedMessageTemplates(img)}
                                title={hasLinkedMessageTemplates(img) ? '연결된 메시지 템플릿 보기' : '연결된 메시지 템플릿이 없습니다.'}
                                className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    void openLinkedMessagesModal(img.id)
                                }}
                            >
                                연결된 메시지 보기
                            </button>
                            {isOperator && (
                                <div className="mt-2 space-y-1.5">
                                    {img.reviewStatus === 'PENDING_REVIEW' && (
                                        <div className="grid grid-cols-2 gap-1.5">
                                            <button
                                                type="button"
                                                className="rounded bg-emerald-600 px-2 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                                                disabled={reviewingId != null}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    void reviewImageEntry(img, 'approve')
                                                }}
                                            >
                                                {reviewingId === img.id ? '처리 중…' : '승인'}
                                            </button>
                                            <button
                                                type="button"
                                                className="rounded border border-red-300 px-2 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
                                                disabled={reviewingId != null}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    void reviewImageEntry(img, 'reject')
                                                }}
                                            >
                                                반려
                                            </button>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        className="w-full rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs font-medium text-red-800 hover:bg-red-100 disabled:opacity-50"
                                        disabled={deletingId != null}
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            void deleteImageEntry(img)
                                        }}
                                    >
                                        {deletingId === img.id ? '삭제 중…' : '삭제'}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {isOperator && historyOpen && (
                <div className="mt-4 rounded border bg-white p-3">
                    <div className="text-sm font-semibold mb-2">이전 이미지 생성 프롬프트 기록</div>
                    {images.length === 0 ? (
                        <div className="text-xs text-slate-500">기록이 없습니다.</div>
                    ) : (
                        <div className="space-y-2 max-h-60 overflow-auto">
                            {images.map((img) => (
                                <div key={`hist-${img.id}`} className="rounded border px-3 py-2">
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="text-xs text-slate-500">{fmt(img.createdAt)}</div>
                                        <button
                                            type="button"
                                            className="rounded border px-2 py-1 text-xs hover:bg-slate-50"
                                            onClick={() => {
                                                setSelectedHistory({
                                                    planningPrompt: img.planningPrompt || '',
                                                    renderPrompt: img.renderPrompt || '',
                                                    prompt: img.prompt || ''
                                                })
                                                setHistoryPromptTab(img.planningPrompt ? 'planning' : (img.renderPrompt ? 'render' : 'final'))
                                                setPromptHistoryOpen(true)
                                            }}
                                        >
                                            프롬프트 보기
                                        </button>
                                    </div>
                                    <div className="text-xs text-slate-700 mt-1 line-clamp-2">
                                        {img.prompt || '프롬프트 없음'}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {isOperator && promptModalOpen && (
                <div className="fixed inset-0 z-[130]">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setPromptModalOpen(false)} />
                    <div className="absolute inset-0 flex items-center justify-center p-4 overflow-y-auto">
                        <div className="w-full max-w-3xl max-h-[min(90dvh,calc(100vh-2rem))] flex flex-col rounded-lg bg-white shadow-lg border my-auto">
                            <div className="flex shrink-0 items-center justify-between px-4 py-3 border-b gap-2">
                                <div className="font-semibold min-w-0">프롬프트 생성 및 이미지 생성 ({currentCategory})</div>
                                <button
                                    type="button"
                                    className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 shrink-0"
                                    onClick={() => {
                                        setPromptModalOpen(false)
                                        setGeneratedPrompt('')
                                    }}
                                >
                                    닫기
                                </button>
                            </div>
                            <div className="p-4 space-y-4 overflow-y-auto min-h-0 flex-1">
                                {error && (
                                    <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                        {error}
                                    </div>
                                )}
                                {modelFailureLines.length > 0 && (
                                    <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 space-y-1">
                                        <div className="font-medium">모델별 실패 요약</div>
                                        {modelFailureLines.map((line, idx) => (
                                            <div key={`${line}-${idx}`}>- {line}</div>
                                        ))}
                                    </div>
                                )}

                                <div>
                                    <div className="text-sm font-medium mb-2">세부 설정</div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
                                        <select
                                            className="rounded border px-3 py-2 text-sm md:col-span-2"
                                            value={currentCategory}
                                            onChange={(e) => applyPromptCategory(e.target.value)}
                                        >
                                            {tabs.map((tab) => (
                                                <option key={tab} value={tab}>카테고리: {tab}</option>
                                            ))}
                                        </select>
                                        <select className="rounded border px-3 py-2 text-sm" value={promptProcessType} onChange={(e) => setPromptProcessType(e.target.value)}>
                                            {processTypeOptions.map((v) => <option key={v} value={v}>{v} 플로우</option>)}
                                        </select>
                                        <select className="rounded border px-3 py-2 text-sm" value={promptStyle} onChange={(e) => setPromptStyle(e.target.value)}>
                                            {styleOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                        <div>
                                            <div className="text-xs text-slate-600 mb-1">하위 주제</div>
                                            <select
                                                className="w-full rounded border px-3 py-2 text-sm"
                                                aria-label="하위 주제"
                                                value={promptTopic}
                                                onChange={(e) => setPromptTopic(e.target.value)}
                                            >
                                                {(topicOptionsByCategory[currentCategory] || []).map((v) => (
                                                    <option key={v} value={v}>{v}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <select className="rounded border px-3 py-2 text-sm" value={promptAudience} onChange={(e) => setPromptAudience(e.target.value)}>
                                            <option value="">대상 고객 선택</option>
                                            {audienceOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                        <select className="rounded border px-3 py-2 text-sm" value={promptCardCount} onChange={(e) => setPromptCardCount(e.target.value)}>
                                            {cardCountOptions.map((v) => <option key={v} value={v}>{v}장</option>)}
                                        </select>
                                        <select className="rounded border px-3 py-2 text-sm" value={promptRatio} onChange={(e) => setPromptRatio(e.target.value)}>
                                            {ratioOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                                        </select>
                                        <div>
                                            <div className="text-xs text-slate-600 mb-1">세부 주제 (preset)</div>
                                            <select
                                                className="w-full rounded border px-3 py-2 text-sm"
                                                aria-label="세부 주제 preset"
                                                value={promptSubcategory}
                                                onChange={(e) => handlePresetSubcategory(e.target.value)}
                                            >
                                                <option value="">선택</option>
                                                {(subcategoryPresetsByCategory[currentCategory] || []).map((v) => (
                                                    <option key={v} value={v}>{v}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <div className="text-xs text-slate-600 mb-1">세부 주제 (직접 입력)</div>
                                            <input
                                                className="w-full rounded border px-3 py-2 text-sm"
                                                value={promptSubcategory}
                                                onChange={(e) => setPromptSubcategory(e.target.value)}
                                                placeholder="예: 연금, 실손, 건강검진"
                                            />
                                        </div>
                                        <input
                                            className="rounded border px-3 py-2 text-sm"
                                            value={promptKeyMessage}
                                            onChange={(e) => setPromptKeyMessage(e.target.value)}
                                            placeholder="핵심문구 키워드 (예: 연금 준비 시작)"
                                        />
                                        <input
                                            className="rounded border px-3 py-2 text-sm"
                                            value={promptAudience}
                                            onChange={(e) => setPromptAudience(e.target.value)}
                                            placeholder="대상 고객 (예: 3040 가장)"
                                        />
                                        <input
                                            className="rounded border px-3 py-2 text-sm"
                                            value={promptTone}
                                            onChange={(e) => setPromptTone(e.target.value)}
                                            placeholder="톤 (예: 따뜻하고 전문적)"
                                        />
                                    </div>
                                    <div className="text-sm font-medium mb-2">기획 프롬프트(1단계)</div>
                                    {planningPrompt ? (
                                        <textarea
                                            className="w-full rounded border px-3 py-2 text-sm min-h-[120px] mb-3"
                                            value={planningPrompt}
                                            onChange={(e) => setPlanningPrompt(e.target.value)}
                                            placeholder="기획 프롬프트가 여기에 표시됩니다..."
                                        />
                                    ) : null}
                                    <div className="text-sm font-medium mb-2">생성된 프롬프트</div>
                                    {generatingPrompt ? (
                                        <div className="rounded border bg-slate-50 p-4 text-sm text-slate-600">
                                            프롬프트 생성 중...
                                        </div>
                                    ) : generatedPrompt ? (
                                        <textarea
                                            className="w-full rounded border px-3 py-2 text-sm min-h-[200px]"
                                            value={generatedPrompt}
                                            onChange={(e) => setGeneratedPrompt(e.target.value)}
                                            placeholder="프롬프트가 여기에 표시됩니다..."
                                        />
                                    ) : (
                                        <div className="rounded border bg-slate-50 p-4 text-sm text-slate-600">
                                            프롬프트를 생성해주세요.
                                        </div>
                                    )}
                                </div>

                                <div>
                                    <div className="text-sm font-medium mb-2">이미지 생성 엔진</div>
                                    <select
                                        className="w-full rounded border px-3 py-2 text-sm mb-2"
                                        value={imageProvider}
                                        onChange={(e) => {
                                            const v = e.target.value
                                            setImageProvider(v)
                                            if (v === 'openai') {
                                                setModelCandidates(openAiModelOptions.map((x) => x.value))
                                                setSelectedModel('gpt-image-2')
                                            } else {
                                                setSelectedModel('gemini-3.1-flash-image-preview')
                                                void loadGeminiModelCandidates()
                                            }
                                        }}
                                    >
                                        <option value="gemini">Google Gemini (기본)</option>
                                        <option value="openai">OpenAI GPT Image 2</option>
                                    </select>
                                    <div className="text-xs text-slate-500 mb-3">
                                        GPT Image 2는 OpenAI API 키와 조직 검증 요건이 있을 수 있습니다. 동일 1024×1024 기준
                                        공식 가이드상 Gemini Flash Image(약 $0.067/장)와 GPT Image 2 medium(약 $0.053/장)은
                                        비슷한 수준이며, quality·size에 따라 OpenAI 쪽이 달라집니다.
                                    </div>
                                    {imageProvider === 'openai' && (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                                            <div>
                                                <div className="text-xs text-slate-600 mb-1">OpenAI 품질 (quality)</div>
                                                <select
                                                    className="w-full rounded border px-3 py-2 text-sm"
                                                    value={openaiImageQuality}
                                                    onChange={(e) => setOpenaiImageQuality(e.target.value)}
                                                >
                                                    {openAiQualityOptions.map((q) => (
                                                        <option key={q} value={q}>{q}</option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div>
                                                <div className="text-xs text-slate-600 mb-1">OpenAI 크기 (size)</div>
                                                <select
                                                    className="w-full rounded border px-3 py-2 text-sm"
                                                    value={openaiImageSize}
                                                    onChange={(e) => setOpenaiImageSize(e.target.value)}
                                                >
                                                    {openAiSizeOptions.map((sz) => (
                                                        <option key={sz} value={sz}>{sz}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                    <div className="text-sm font-medium mb-2">이미지 생성 모델(선택)</div>
                                    {loadingModels ? (
                                        <div className="text-xs text-slate-500">모델 조회 중...</div>
                                    ) : (
                                        <select
                                            className="w-full rounded border px-3 py-2 text-sm"
                                            value={selectedModel}
                                            onChange={(e) => setSelectedModel(e.target.value)}
                                        >
                                            {modelCandidates.map((m) => (
                                                <option key={m} value={m}>
                                                    {fixedModelOptions.find((x) => x.value === m)?.label
                                                        || openAiModelOptions.find((x) => x.value === m)?.label
                                                        || m}
                                                </option>
                                            ))}
                                        </select>
                                    )}
                                    <div className="text-xs text-slate-500 mt-1">
                                        {imageProvider === 'openai'
                                            ? '위에서 quality·size를 바꿀 수 있습니다. 미선택 시 서버 기본값(openai.image-quality / openai.image-size)을 씁니다.'
                                            : '기본은 3.1 Flash Image Preview이며, 필요 시 2.5 또는 3.1 Pro로 변경해 테스트할 수 있습니다.'}
                                    </div>
                                    {Number(promptCardCount) > 1 && (
                                        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 mt-2">
                                            장수가 2~4이면 이미지 API를 장수만큼 순차 호출합니다. 비용·소요 시간이 장수에 비례합니다.
                                        </div>
                                    )}
                                </div>

                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        className="rounded border px-4 py-2 text-sm hover:bg-slate-50"
                                        onClick={() => handleGeneratePrompt(currentCategory)}
                                        disabled={generatingPrompt}
                                    >
                                        {generatingPrompt ? '생성 중...' : (generatedPrompt ? '프롬프트 다시 생성' : '프롬프트 생성')}
                                    </button>
                                    <button
                                        type="button"
                                        className="flex-1 rounded bg-teal-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-teal-800 disabled:opacity-50"
                                        onClick={handleGenerateImage}
                                        disabled={!generatedPrompt || generatingImage || generatingPrompt}
                                    >
                                        {generatingImage
                                            ? '이미지 생성 중...'
                                            : (Number(promptCardCount) > 1
                                                ? `이미지 ${promptCardCount}장 생성 (순차 호출)`
                                                : '이미지 생성하기')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {isOperator && promptHistoryOpen && (
                <div className="fixed inset-0 z-[135]">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setPromptHistoryOpen(false)} />
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                        <div className="w-full max-w-3xl rounded-lg bg-white shadow-lg border">
                            <div className="flex items-center justify-between px-4 py-3 border-b">
                                <div className="font-semibold">프롬프트 기록</div>
                                <button type="button" className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={() => setPromptHistoryOpen(false)}>
                                    닫기
                                </button>
                            </div>
                            <div className="p-4">
                                <div className="mb-2 flex gap-2">
                                    <button
                                        type="button"
                                        className={`rounded border px-2 py-1 text-xs ${historyPromptTab === 'planning' ? 'bg-slate-100 font-semibold' : 'hover:bg-slate-50'}`}
                                        onClick={() => setHistoryPromptTab('planning')}
                                    >
                                        1단계 기획
                                    </button>
                                    <button
                                        type="button"
                                        className={`rounded border px-2 py-1 text-xs ${historyPromptTab === 'render' ? 'bg-slate-100 font-semibold' : 'hover:bg-slate-50'}`}
                                        onClick={() => setHistoryPromptTab('render')}
                                    >
                                        2단계 정제
                                    </button>
                                    <button
                                        type="button"
                                        className={`rounded border px-2 py-1 text-xs ${historyPromptTab === 'final' ? 'bg-slate-100 font-semibold' : 'hover:bg-slate-50'}`}
                                        onClick={() => setHistoryPromptTab('final')}
                                    >
                                        최종 사용
                                    </button>
                                </div>
                                <textarea
                                    className="w-full rounded border px-3 py-2 text-sm min-h-[280px]"
                                    value={
                                        historyPromptTab === 'planning'
                                            ? (selectedHistory?.planningPrompt || '')
                                            : historyPromptTab === 'render'
                                                ? (selectedHistory?.renderPrompt || '')
                                                : (selectedHistory?.prompt || '')
                                    }
                                    readOnly
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {imageZoomOpen && zoomImageUrl && (
                <div className="fixed inset-0 z-[136]">
                    <div className="absolute inset-0 bg-black/70" onClick={closeImageZoom} />
                    <div className="absolute inset-0 flex items-center justify-center p-3 sm:p-4">
                        <div className="w-full max-w-5xl max-h-[min(92vh,40rem)] flex flex-col rounded-lg bg-white shadow-lg border overflow-hidden">
                            <div className="flex items-center justify-between gap-3 px-4 py-3 border-b flex-wrap shrink-0">
                                <div className="min-w-0">
                                    <div className="font-semibold truncate">{zoomImage?.imageName || zoomImage?.category || '이미지'}</div>
                                    <div className="text-xs text-slate-500 mt-0.5">
                                        {[zoomImage?.category, zoomImage?.createdAt ? fmt(zoomImage.createdAt) : null]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5">{formatStatLine(zoomImage)}</div>
                                    {zoomImage?.seriesCardIndex != null && (
                                        <div className="text-xs text-slate-600 mt-1">시리즈 카드 순번: {zoomImage.seriesCardIndex}</div>
                                    )}
                                </div>
                                <div className="flex flex-wrap items-center gap-2 shrink-0">
                                    <button
                                        type="button"
                                        disabled={!hasLinkedMessageTemplates(zoomImage)}
                                        title={hasLinkedMessageTemplates(zoomImage) ? '연결된 메시지 템플릿 보기' : '연결된 메시지 템플릿이 없습니다.'}
                                        className="rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                                        onClick={() => void openLinkedMessagesModal(zoomImage?.id)}
                                    >
                                        연결된 메시지 보기
                                    </button>
                                    <button
                                        type="button"
                                        className="rounded bg-brand-navy text-white px-3 py-1.5 text-sm hover:opacity-90 disabled:opacity-40"
                                        onClick={() => void downloadZoomFile()}
                                        disabled={!zoomImage?.fileId}
                                    >
                                        다운로드
                                    </button>
                                    {isOperator && (
                                        <button
                                            type="button"
                                            className="rounded border border-red-200 bg-red-50 px-3 py-1.5 text-sm text-red-800 hover:bg-red-100 disabled:opacity-50"
                                            disabled={deletingId != null || !zoomImage?.id}
                                            onClick={() => void deleteImageEntry(zoomImage)}
                                        >
                                            {deletingId === zoomImage?.id ? '삭제 중…' : '삭제'}
                                        </button>
                                    )}
                                    <button type="button" className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50" onClick={closeImageZoom}>
                                        닫기
                                    </button>
                                </div>
                            </div>
                            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
                                <div className="flex justify-center items-start max-h-[min(48vh,22rem)] w-full overflow-hidden rounded border bg-slate-50">
                                    <img
                                        src={zoomImageUrl}
                                        alt="zoom"
                                        className="max-h-[min(48vh,22rem)] max-w-full w-auto h-auto object-contain"
                                    />
                                </div>
                                {zoomImage?.caption && !isOperator && (
                                    <div className="rounded border bg-slate-50 px-3 py-2 text-sm text-slate-800 whitespace-pre-wrap max-h-40 overflow-y-auto">
                                        {zoomImage.caption}
                                    </div>
                                )}
                                {isOperator && (
                                    <div className="min-h-0">
                                        <div className="text-sm font-medium text-slate-800 mb-1">캡션 · 발송 문구</div>
                                        <textarea
                                            className="w-full rounded border px-3 py-2 text-sm max-h-36 min-h-[80px] overflow-y-auto"
                                            value={zoomCaptionDraft}
                                            onChange={(e) => setZoomCaptionDraft(e.target.value)}
                                            placeholder="이미지와 함께 보낼 카카오·문자 문구 등을 적어두면 자료실 목록과 여기에 표시됩니다."
                                        />
                                        <button
                                            type="button"
                                            className="mt-2 rounded bg-brand-navy text-white px-3 py-1.5 text-sm hover:opacity-90 disabled:opacity-50"
                                            disabled={savingCaption || !zoomImage?.id}
                                            onClick={() => void saveZoomCaption()}
                                        >
                                            {savingCaption ? '저장 중…' : '캡션 저장'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {linkedMsgOpen && (
                <div className="fixed inset-0 z-[138] flex items-center justify-center p-4 bg-black/40" onClick={closeLinkedMessagesModal}>
                    <div
                        className="w-full max-w-lg max-h-[min(calc(85vh * 1.2),calc(32rem * 1.2))] rounded-lg bg-white shadow-xl border flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
                            <div className="font-semibold text-slate-900">연결된 메시지 템플릿</div>
                            <button type="button" className="rounded border px-3 py-1 text-sm hover:bg-slate-50" onClick={closeLinkedMessagesModal}>
                                닫기
                            </button>
                        </div>
                        <div className="flex-1 min-h-0 overflow-y-auto p-4 text-sm">
                            {linkedMsgLoading && <div className="text-slate-600">불러오는 중…</div>}
                            {linkedMsgError && <div className="text-red-700">{linkedMsgError}</div>}
                            {!linkedMsgLoading && !linkedMsgError && linkedMsgRows.length === 0 && (
                                <div className="text-slate-600">이 이미지를 연결한 메시지 템플릿이 없습니다.</div>
                            )}
                            {!linkedMsgLoading && linkedMsgRows.map((row) => (
                                <div key={row.id} className="rounded border border-slate-200 p-3 mb-3 last:mb-0">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="font-medium text-slate-900 min-w-0 flex-1">{row.title || '제목 없음'}</div>
                                        <button
                                            type="button"
                                            className="shrink-0 rounded border border-slate-300 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-50"
                                            onClick={() => void copyLinkedMessageTemplate(row)}
                                        >
                                            {linkedMsgCopiedId === row.id ? '복사됨' : '복사하기'}
                                        </button>
                                    </div>
                                    {/* <div className="text-xs text-slate-500 mt-1 flex flex-wrap gap-2">
                                        {row.status && <span>상태: {row.status}</span>}
                                        {row.templateType && <span>유형: {row.templateType}</span>}
                                        {row.category && <span>카테고리: {row.category}</span>}
                                        {row.updatedAt && <span>수정: {fmt(row.updatedAt)}</span>}
                                    </div> */}
                                    {row.contentPreview != null && String(row.contentPreview).length > 0 && (
                                        <pre className="mt-2 text-xs text-slate-700 whitespace-pre-wrap break-words max-h-[14.4rem] overflow-y-auto font-sans bg-slate-50 rounded px-2 py-1.5 border border-slate-100">
                                            {row.contentPreview}
                                        </pre>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {isOperator && uploadModalOpen && (
                <div className="fixed inset-0 z-[131]">
                    <div className="absolute inset-0 bg-black/40" onClick={() => !uploadSubmitting && setUploadModalOpen(false)} />
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                        <div className="w-full max-w-md rounded-lg bg-white shadow-lg border">
                            <div className="flex items-center justify-between px-4 py-3 border-b">
                                <div className="font-semibold">이미지 직접 업로드</div>
                                <button
                                    type="button"
                                    className="rounded border px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50"
                                    disabled={uploadSubmitting}
                                    onClick={() => setUploadModalOpen(false)}
                                >
                                    닫기
                                </button>
                            </div>
                            <div className="p-4 space-y-3">
                                {error && <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
                                <div>
                                    <div className="text-xs text-slate-600 mb-1">카테고리</div>
                                    <select
                                        className="w-full rounded border px-3 py-2 text-sm"
                                        value={uploadCategory}
                                        onChange={(e) => setUploadCategory(e.target.value)}
                                    >
                                        {tabs.map((tab) => (
                                            <option key={tab} value={tab}>{tab}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <div className="text-xs text-slate-600 mb-1">파일 (이미지)</div>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="w-full text-sm"
                                        onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                    />
                                </div>
                                <div>
                                    <div className="text-xs text-slate-600 mb-1">캡션 (선택)</div>
                                    <textarea
                                        className="w-full rounded border px-3 py-2 text-sm min-h-[80px]"
                                        value={uploadCaption}
                                        onChange={(e) => setUploadCaption(e.target.value)}
                                        placeholder="발송 시 함께 쓸 문구를 미리 적어둘 수 있습니다."
                                    />
                                </div>
                                <button
                                    type="button"
                                    className="w-full rounded bg-brand-navy text-white px-4 py-2 text-sm hover:opacity-90 disabled:opacity-50"
                                    disabled={uploadSubmitting}
                                    onClick={() => void submitLibraryUpload()}
                                >
                                    {uploadSubmitting ? '업로드 중…' : '업로드'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
